import React, { useRef } from 'react';
import './CubeRenderer.css';

interface CubeRendererProps {
  grid: number[][];
  size?: number;
}

const CubeRenderer: React.FC<CubeRendererProps> = ({ grid, size = 400 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const cubeSize = 40;
  const centerX = size / 2;
  const centerY = size / 2;

  const downloadAsPNG = () => {
    if (!svgRef.current) return;

    const svgElement = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#fafafa';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `cube-configuration-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        URL.revokeObjectURL(svgUrl);
      }, 'image/png');
    };

    img.src = svgUrl;
  };

  const renderCube = (row: number, col: number, z: number) => {
    // Adjusted viewpoint angle - slightly asymmetric for better distinction
    const angleX = Math.PI / 6.5; // ~28° for X-axis
    const angleY = Math.PI / 7.5; // ~24° for Y-axis (different angle)
    const w = cubeSize * Math.cos(angleX); // ~35
    const h = cubeSize * Math.sin(angleY); // ~15
    
    // Calculate position
    const isoX = (col - row) * w;
    const isoY = (col + row) * h - z * cubeSize; // Subtract z to stack upward
    
    const x = centerX + isoX;
    const y = centerY + isoY;
    
    return (
      <g key={`${row}-${col}-${z}`}>
        {/* Top face - diamond shape */}
        <polygon
          points={`
            ${x},${y}
            ${x + w},${y + h}
            ${x},${y + 2 * h}
            ${x - w},${y + h}
          `}
          fill="#ffffff"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        
        {/* Right face */}
        <polygon
          points={`
            ${x},${y + 2 * h}
            ${x + w},${y + h}
            ${x + w},${y + h + cubeSize}
            ${x},${y + 2 * h + cubeSize}
          `}
          fill="#d8d8d8"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        
        {/* Left face */}
        <polygon
          points={`
            ${x - w},${y + h}
            ${x},${y + 2 * h}
            ${x},${y + 2 * h + cubeSize}
            ${x - w},${y + h + cubeSize}
          `}
          fill="#b0b0b0"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </g>
    );
  };

  const renderAllCubes = () => {
    const cubes: React.ReactElement[] = [];
    const positions: { row: number; col: number; z: number }[] = [];
    
    // Collect all cube positions
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const height = grid[row][col];
        for (let z = 0; z < height; z++) {
          positions.push({ row, col, z });
        }
      }
    }
    
    // Sort for proper rendering order (back to front, right to left, bottom to top)
    positions.sort((a, b) => {
      const sumA = a.row + a.col;
      const sumB = b.row + b.col;
      if (sumA !== sumB) return sumA - sumB;
      
      if (a.row !== b.row) return a.row - b.row;
      if (a.col !== b.col) return b.col - a.col;
      return a.z - b.z;
    });
    
    // Render each cube
    positions.forEach(pos => {
      cubes.push(renderCube(pos.row, pos.col, pos.z));
    });
    
    return cubes;
  };

  return (
    <div className="cube-renderer">
      <svg 
        ref={svgRef}
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        style={{ backgroundColor: '#fafafa' }}
      >
        {renderAllCubes()}
      </svg>
      <button 
        onClick={downloadAsPNG}
        style={{
          marginTop: '10px',
          padding: '8px 16px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        Download as PNG
      </button>
    </div>
  );
};

export default CubeRenderer;